import type { BrandFeature, MeOut } from './api/generated'

/** The feature vocabulary is generated from ``BrandFeature``; keep this exhaustive for legacy-session fallback. */
export const BRAND_FEATURES = [
  'work_orders',
  'contractors',
  'billing',
  'hardware',
  'licences',
  'schedule',
  'satisfaction',
  'crm_deals',
  'chat_widget',
  'customer_acks',
  'desk_projection',
] as const satisfies readonly BrandFeature[]

type BrandFeatureMe = Pick<MeOut, 'brands'>

/**
 * Features enabled in the selected brand scope. ``all`` is the union, not an intersection: a tool remains reachable
 * when it can operate on at least one visible brand. A named brand that is missing from ``MeOut.brands`` gets none.
 *
 * Sessions cached by a pre-brands web build have no ``brands`` field. Preserve that build's Ewee UI until the next
 * session read supplies the authoritative feature list; an explicit empty list still means no accessible features.
 */
export function brandFeatures(me: BrandFeatureMe | null | undefined, selectedSlug: string | null | undefined): Set<BrandFeature> {
  if (!me || me.brands === undefined) return new Set(BRAND_FEATURES)
  const brands = selectedSlug && selectedSlug !== 'all' ? me.brands.filter((brand) => brand.slug === selectedSlug) : me.brands
  return new Set(brands.flatMap((brand) => brand.features ?? []))
}

export function hasBrandFeatures(enabled: ReadonlySet<BrandFeature>, required: readonly BrandFeature[] | undefined): boolean {
  return required === undefined || required.every((feature) => enabled.has(feature))
}
